import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, User, LogOut, ChevronDown, ChevronRight, Building2, Mail, LayoutDashboard, BookOpen, Home, FileText, Landmark, Grid3X3, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationDropdown from './NotificationDropdown';

interface NavigationProps {
  onMenuClick?: () => void;
  variant?: 'light' | 'dark';
  showDmvRecords?: boolean;
}

// Breadcrumb mapping for routes
const routeNames: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/record-inquiry-log': 'Reports',
  '/nmvtis-record': 'NMVTIS Record',
  '/lien-theft-record': 'Theft Check',
  '/motor-vehicle-record': 'Motor Vehicle Record',
  '/vin-decode': 'VIN Decoder',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/notifications': 'Notifications',
  '/notification-management': 'Notification Management',
  '/pricing-management': 'Pricing',
  '/margins': 'Margins',
  '/management': 'Organization',
  '/profile': 'Profile',
  '/admin/problem-reports': 'Problem Reports',
  '/admin/jurisdictions': 'Jurisdictions',
  '/admin/clients': 'Clients',
  '/admin/application-settings': 'Application Settings',
  '/report-issue': 'Report Issue',
};

const Navigation: React.FC<NavigationProps> = ({ onMenuClick, variant = 'light', showDmvRecords = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Check if we're on pages that should have dark navbar (home, login, register, contact, use-case, about, terms, privacy, product pages)
  // Terms and Privacy render on the same bg-slate-950 body as every other public page, so leaving
  // them out gave them a white bar on a near-black page and dropped the centre product nav, which
  // is gated on this same flag.
  const isDarkPage = ['/', '/login', '/register', '/contact', '/use-case', '/about', '/terms', '/privacy', '/products/vehicle-history', '/products/dmv-records', '/products/vin-decode', '/products/theft-lien-check'].includes(location.pathname);

  // Dark mode if variant is dark OR we're on a dark page
  const isDark = variant === 'dark' || isDarkPage;

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = async () => {
    await logout();
    setIsDropdownOpen(false);
    navigate('/login');
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsDropdownOpen(false);
  };

  // Get user initials
  const getUserInitials = () => {
    if (!user) return '';
    const first = user.firstName?.charAt(0) || '';
    const last = user.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  // Format time
  const formatTime = () => {
    return currentTime.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  // Get timezone abbreviation
  const getTimezone = () => {
    return currentTime.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
  };

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    const path = location.pathname;
    const crumbs: { name: string; path: string }[] = [];

    // Always start with Dashboard for authenticated users
    if (isAuthenticated && path !== '/dashboard') {
      crumbs.push({ name: 'Dashboard', path: '/dashboard' });
    }

    // Add current page
    const currentName = routeNames[path];
    if (currentName && path !== '/dashboard') {
      crumbs.push({ name: currentName, path });
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  // Check if we're on an authenticated page (not home, login, etc.)
  // Also check for routes with dynamic segments (e.g., /admin/problem-reports/:id)
  const authenticatedRoutePrefixes = ['/dashboard', '/record-inquiry-log', '/nmvtis-record', '/lien-theft-record', '/motor-vehicle-record', '/vin-decode', '/reports', '/settings', '/notifications', '/notification-management', '/pricing-management', '/margins', '/management', '/profile', '/admin/', '/report-issue', '/invoicing'];
  const isAuthenticatedPage = isAuthenticated && user && authenticatedRoutePrefixes.some(prefix => location.pathname.startsWith(prefix));

  return (
    <>
      {/* Top navigation bar */}
      <div className={`fixed top-0 left-0 right-0 z-40 border-b ${
        isDarkPage
          ? 'bg-black border-white/10'
          : isDark
            ? 'bg-black/90 border-white/10 backdrop-blur-xl'
            : 'bg-white/80 border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.05)] backdrop-blur-xl'
      }`}>
        <div className="flex items-center justify-between px-4 md:px-6 py-3">
          {/* Left side: Hamburger Menu (authenticated users only) + Logo + Breadcrumbs */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && user && onMenuClick && (
              <button
                onClick={onMenuClick}
                className={`lg:hidden p-2 rounded-lg transition-colors ${
                  isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                }`}
                aria-label="Open navigation menu"
              >
                <Menu className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#475569]'}`} />
              </button>
            )}
            <Link to="/" className="flex items-center no-underline">
              <div className="flex flex-col">
                <img
                  src={isDark ? "/aasWhiteLetters.png" : "/aaslogo.png"}
                  alt="Auto Access Solutions"
                  className="h-10 md:h-12"
                />
                <div className={`hidden md:flex flex-col text-center mt-0.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                  <span className={`text-[9px] leading-tight ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>an Approved NMVTIS Data Provider</span>
                </div>
              </div>
            </Link>

            {/* Breadcrumbs (only on authenticated pages) */}
            {isAuthenticatedPage && breadcrumbs.length > 0 && (
              <>
                <ChevronRight className="hidden md:block w-4 h-4 text-[#94a3b8]" />
                <div className="hidden md:flex items-center space-x-2 text-sm">
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      {index > 0 && <ChevronRight className="w-4 h-4 text-[#94a3b8]" />}
                      {index === breadcrumbs.length - 1 ? (
                        <span className="text-[#0f172a] font-medium">{crumb.name}</span>
                      ) : (
                        <Link
                          to={crumb.path}
                          className="text-[#64748b] hover:text-[#0f172a] transition-colors no-underline"
                        >
                          {crumb.name}
                        </Link>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}

          </div>

          {/* Center: Product Navigation Links (on dark/landing pages) */}
          {isDarkPage && (
            <nav className="hidden lg:flex items-center space-x-1 absolute left-1/2 -translate-x-1/2">
              {[
                { icon: FileText, label: 'Vehicle History', href: '/products/vehicle-history' },
                ...(showDmvRecords ? [{ icon: Landmark, label: 'DMV Records', href: '/products/dmv-records' }] : []),
                { icon: Grid3X3, label: 'Intelligent VIN Decode', href: '/products/vin-decode' },
                { icon: AlertTriangle, label: 'Theft/Lien Check', href: '/products/theft-lien-check' },
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className="group flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-200 no-underline"
                >
                  <item.icon className="w-4 h-4 text-teal-400 group-hover:text-white transition-colors duration-200" />
                  <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors duration-200 whitespace-nowrap">
                    {item.label}
                  </span>
                </Link>
              ))}
            </nav>
          )}

          {/* Right side: Time, Notifications, User Menu */}
          <div className="flex items-center space-x-4">
            {/* Time Display (only on authenticated pages) */}
            {isAuthenticatedPage && (
              <div className="hidden lg:flex items-center text-sm text-[#64748b]">
                <span className="text-[#94a3b8] mr-1">{getTimezone()}</span>
                <span className="font-mono text-[#c41e3a] font-medium">{formatTime()}</span>
              </div>
            )}

            {/* Notification Dropdown (only on authenticated pages) */}
            {isAuthenticatedPage && <NotificationDropdown />}

            {isAuthenticated && user ? (
              // Logged In - User Button with Dropdown
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`flex items-center space-x-2 rounded-xl px-2 py-1.5 transition-colors ${
                    isDark ? 'hover:bg-white/10' : 'hover:bg-black/[0.03]'
                  }`}
                >
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#c41e3a] to-[#a01830] flex items-center justify-center text-white font-semibold text-sm">
                    {getUserInitials()}
                  </div>
                  <span className={`hidden md:inline font-medium text-sm ${isDark ? 'text-white' : 'text-[#0f172a]'}`}>{user.firstName} {user.lastName}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isDark ? 'text-white/70' : 'text-[#64748b]'} ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Logged In Dropdown */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-xl rounded-xl border border-black/[0.08] shadow-xl overflow-hidden z-50 animate-slide-down">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-black/[0.06] bg-gradient-to-br from-[#f8fafc] to-white">
                      <p className="text-sm text-[#0f172a] font-semibold">{user.firstName} {user.lastName}</p>
                      <p className="text-xs text-[#64748b]">{user.email}</p>
                      <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#c41e3a]/10 text-[#c41e3a] border border-[#c41e3a]/20">
                        {user.role}
                      </span>
                    </div>

                    {/* User Actions with Icons */}
                    <div className="py-1.5">
                      <button
                        onClick={() => handleNavClick('/')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Home className="w-4 h-4" />
                        <span className="text-sm font-medium">Home</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/dashboard')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        <span className="text-sm font-medium">Dashboard</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/profile')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <User className="w-4 h-4" />
                        <span className="text-sm font-medium">Profile</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/use-case')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <BookOpen className="w-4 h-4" />
                        <span className="text-sm font-medium">Use Case</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/about')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Building2 className="w-4 h-4" />
                        <span className="text-sm font-medium">About Us</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/contact')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm font-medium">Contact</span>
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-black/[0.06]"></div>

                    {/* Log Out */}
                    <div className="py-1.5">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 text-[#dc2626] hover:bg-[#dc2626]/5 transition-colors flex items-center space-x-3"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm font-medium">Log out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Not Logged In - Menu Button with Dropdown
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`backdrop-blur-sm px-4 py-2 md:px-5 md:py-2.5 rounded-xl transition-all border flex items-center space-x-2 ${
                    isDark
                      ? 'bg-white/10 border-white/20 hover:bg-white/20'
                      : 'bg-white/70 border-black/[0.08] hover:bg-white hover:shadow-sm'
                  }`}
                >
                  <Menu className={`w-5 h-5 ${isDark ? 'text-white' : 'text-[#475569]'}`} />
                  <span className={`hidden md:inline font-medium ${isDark ? 'text-white' : 'text-[#0f172a]'}`}>Menu</span>
                </button>

                {/* Not Logged In Dropdown */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-xl rounded-xl border border-black/[0.08] shadow-xl overflow-hidden z-50 animate-slide-down">
                    {/* Navigation Links */}
                    <div className="py-1.5">
                      <button
                        onClick={() => handleNavClick('/')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Home className="w-4 h-4" />
                        <span className="text-sm font-medium">Home</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/about')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Building2 className="w-4 h-4" />
                        <span className="text-sm font-medium">About Us</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/use-case')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <BookOpen className="w-4 h-4" />
                        <span className="text-sm font-medium">Use Case</span>
                      </button>
                      <button
                        onClick={() => handleNavClick('/contact')}
                        className="w-full text-left px-4 py-2.5 text-[#475569] hover:text-[#0f172a] hover:bg-black/[0.03] transition-colors flex items-center space-x-3"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm font-medium">Contact</span>
                      </button>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-black/[0.06]"></div>

                    {/* Login */}
                    <div className="p-3">
                      <button
                        onClick={() => handleNavClick('/login')}
                        className="w-full btn-primary text-center"
                      >
                        Log in
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
